import { create } from "zustand";

export interface BillingWarningState {
  severity: string | null;
  dismissed: boolean;
  setBillingWarning: (severity: string | null) => void;
  dismiss: () => void;
}

const SESSION_DISMISS_KEY = "linkhr:billing-warning-dismissed";

function readInitialDismissed(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
  } catch {
    // Safari private mode / SSR safety (Pitfall 5)
    return false;
  }
}

/**
 * BillingWarning store — shared across @linkhr/api-client billing middleware
 * (setter) and @linkhr/design-system BillingBanner pattern (reader). Store
 * lives here in design-system; api-client middleware receives the setter via
 * <ApiProvider setBillingWarning={useBillingWarningStore.getState().setBillingWarning}>
 * to avoid design-system ↔ api-client cycles (D-20 pattern).
 *
 * Session-dismiss persistence: X button flips `dismissed` + writes
 * sessionStorage sentinel. Cleared when a fresh severity arrives so the banner
 * re-surfaces on state change. sessionStorage survives page reloads; dismiss
 * persists for the lifetime of the tab (D-19).
 */
export const useBillingWarningStore = create<BillingWarningState>((set) => ({
  severity: null,
  dismissed: readInitialDismissed(),
  setBillingWarning: (severity) => {
    // New severity arrives → clear any previous session dismiss so user
    // sees the fresh state.
    if (severity !== null && typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.removeItem(SESSION_DISMISS_KEY);
      } catch {
        /* SSR/private-mode safe */
      }
    }
    set({ severity, dismissed: false });
  },
  dismiss: () => {
    if (typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
      } catch {
        /* SSR/private-mode safe */
      }
    }
    set({ dismissed: true });
  },
}));
